import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, X, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProductSettingsTab() {
    const { getAuthHeader } = useAuth();
    const [categories, setCategories] = useState([]);
    const [pricingModels, setPricingModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newCat, setNewCat] = useState({ value: '', label: '', sku_prefix: '' });
    const [newPM, setNewPM] = useState({ value: '', label: '' });

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/products/settings`, { headers: getAuthHeader() });
                setCategories(res.data.categories || []);
                setPricingModels(res.data.pricing_models || []);
            } catch { toast.error('Failed to load product settings'); }
            finally { setLoading(false); }
        })();
    }, []);

    const save = async (cats, pms) => {
        setSaving(true);
        try {
            await axios.put(`${API}/products/settings`, {
                categories: cats, pricing_models: pms,
            }, { headers: getAuthHeader() });
            toast.success('Product settings saved');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const addCategory = () => {
        if (!newCat.label.trim()) return;
        const value = newCat.value.trim() || newCat.label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        const sku_prefix = (newCat.sku_prefix.trim() || newCat.label.trim().substring(0, 3)).toUpperCase().replace(/[^A-Z0-9]/g, '');
        const updated = [...categories, { value, label: newCat.label.trim(), sku_prefix }];
        setCategories(updated);
        setNewCat({ value: '', label: '', sku_prefix: '' });
        save(updated, pricingModels);
    };

    const updateCategoryPrefix = (idx, prefix) => {
        const updated = categories.map((cat, i) => i === idx ? { ...cat, sku_prefix: prefix.toUpperCase().replace(/[^A-Z0-9]/g, '') } : cat);
        setCategories(updated);
        save(updated, pricingModels);
    };

    const removeCategory = (idx) => {
        const updated = categories.filter((_, i) => i !== idx);
        setCategories(updated);
        save(updated, pricingModels);
    };

    const addPricingModel = () => {
        if (!newPM.label.trim()) return;
        const value = newPM.value.trim() || newPM.label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        const updated = [...pricingModels, { value, label: newPM.label.trim() }];
        setPricingModels(updated);
        setNewPM({ value: '', label: '' });
        save(categories, updated);
    };

    const removePricingModel = (idx) => {
        const updated = pricingModels.filter((_, i) => i !== idx);
        setPricingModels(updated);
        save(categories, updated);
    };

    if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>;

    return (
        <div className="space-y-8 max-w-2xl">
            <div>
                <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Product Settings</h3>
                <p className="text-sm text-stone-500 mt-1">Configure categories and pricing models that appear in the product editor.</p>
            </div>

            {/* Categories */}
            <div>
                <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Product Categories</Label>
                <p className="text-xs text-stone-400 mt-0.5 mb-3">These appear as filter options and category dropdowns when creating products.</p>
                <div className="space-y-1.5 mb-3">
                    {categories.map((cat, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-stone-50 border border-stone-100 rounded-lg">
                            <span className="text-sm font-medium text-stone-700 flex-1">{cat.label}</span>
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-stone-400">SKU prefix:</span>
                                <input
                                    value={cat.sku_prefix || ''}
                                    onChange={e => updateCategoryPrefix(idx, e.target.value)}
                                    placeholder={cat.label.substring(0, 3).toUpperCase()}
                                    className="w-16 px-1.5 py-0.5 text-xs font-mono text-stone-700 bg-white border border-stone-200 rounded text-center uppercase"
                                    maxLength={6}
                                />
                            </div>
                            <button onClick={() => removeCategory(idx)} className="p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <Input
                        value={newCat.label}
                        onChange={e => setNewCat({ ...newCat, label: e.target.value })}
                        placeholder="Category name (e.g., Professional Services)"
                        className="flex-1 text-sm"
                        onKeyDown={e => e.key === 'Enter' && addCategory()}
                    />
                    <Input
                        value={newCat.sku_prefix}
                        onChange={e => setNewCat({ ...newCat, sku_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                        placeholder="SKU (e.g., WKS)"
                        className="w-24 text-sm font-mono uppercase text-center"
                        maxLength={6}
                    />
                    <Button onClick={addCategory} variant="outline" size="sm" disabled={!newCat.label.trim()}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                </div>
            </div>

            {/* Pricing Models */}
            <div>
                <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Pricing Models</Label>
                <p className="text-xs text-stone-400 mt-0.5 mb-3">Define the pricing structures available for your products.</p>
                <div className="space-y-1.5 mb-3">
                    {pricingModels.map((pm, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-stone-50 border border-stone-100 rounded-lg">
                            <span className="text-sm font-medium text-stone-700 flex-1">{pm.label}</span>
                            <span className="text-xs text-stone-400 font-mono">{pm.value}</span>
                            <button onClick={() => removePricingModel(idx)} className="p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <Input
                        value={newPM.label}
                        onChange={e => setNewPM({ ...newPM, label: e.target.value })}
                        placeholder="Pricing model name (e.g., Subscription)"
                        className="flex-1 text-sm"
                        onKeyDown={e => e.key === 'Enter' && addPricingModel()}
                    />
                    <Button onClick={addPricingModel} variant="outline" size="sm" disabled={!newPM.label.trim()}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default function AdminProductSettingsPage() {
    return (
        <AdminLayout title="Product Settings" description="Configure product categories and pricing models">
            <ProductSettingsTab />
        </AdminLayout>
    );
}
